import { BlockReferenceArray } from '@/components/cms/types/block';
import { BlockComponentModel } from '@/components/cms/types/blok-component';
import { SECTION_NAMES } from '@/components/integrations/trackingScripts/constants';

import PricingCards, {
  PricingCardsProps,
  ProductPriceTypes,
  ProductSequantialTypes,
} from '@/decodo/blocks/PricingCards/PricingCards';
import { ButtonColors, ButtonVariants } from '@/decodo/components/atoms/Button';
import { RichTextFieldStrapiProps } from '@/decodo/components/atoms/RichTextField/RichTextFieldStrapi';
import { ToggleProps } from '@/decodo/components/atoms/Toggle';
import { FeatureListStrapiProps } from '@/features/shared/FeatureListStrapiDC';
import { Product } from '@/features/types/product';
import {
  BlockHeader,
  BlockHeaderContent,
} from '@/features/utils/BlockHeaderContentDC';
import { BlockWrapper } from '@/features/utils/BlockWrapperDC';

export type PricingCardsFeatureListStrapiProps = FeatureListStrapiProps & {
  title?: string;
  productType: Product;
};

export interface PricingCardsFeatureListStrapi {
  featuresList: {
    data: Array<{
      id: number;
      attributes: PricingCardsFeatureListStrapiProps;
    }>;
  };
}

export type PricingCardContactSalesProps = BlockReferenceArray<{
  heading?: RichTextFieldStrapiProps;
  description?: RichTextFieldStrapiProps;
  button?: {
    text?: string;
    variant?: ButtonVariants;
    color: ButtonColors;
    link?: string;
  };
  disclaimer?: RichTextFieldStrapiProps;
  productType: Product;
  productPriceCategory: ProductPriceTypes | 'all';
  productSequence: ProductSequantialTypes | 'all';
  outlineColor: 'grey' | 'brand' | 'gradient';
}>;

interface PricingElement {
  card: BlockReferenceArray<PricingCardsProps['cards'][number]>;
}

type PricingCardsStrapiProps = {
  header?: BlockHeader;
  contactSalesCards?: PricingCardContactSalesProps;
  pricing_cards: BlockReferenceArray<PricingCardsProps['cards'][number]>;
  features_references?: PricingCardsFeatureListStrapi['featuresList'];
  toggleVariant: ToggleProps['variant'];
  pricing_cards_group: {
    data: {
      attributes: {
        pricingElements: PricingElement[];
      };
    };
  };
};

export function PricingCardsStrapi({
  block,
  ...rest
}: BlockComponentModel<PricingCardsStrapiProps>) {
  const {
    header,
    pricing_cards,
    features_references,
    toggleVariant,
    pricing_cards_group,
  } = block;

  const formattedCards: PricingCardsProps['cards'] = pricing_cards_group?.data
    ? pricing_cards_group.data.attributes.pricingElements.flatMap((element) => {
        const { card, ...rest } = element;

        if (Array.isArray(card)) {
          return card.map((singleCard) => ({
            ...rest,
            ...singleCard,
          }));
        }

        return [{ ...rest, card }];
      })
    : pricing_cards?.data?.map(({ attributes: card }) => card) || [];

  const distinctPricingCards = formattedCards.reduce(
    (acc: PricingCardsProps['cards'], card) => {
      if (card) {
        const exists = acc.some(
          (c) =>
            c.productPriceCategory === card.productPriceCategory &&
            c.productType === card.productType &&
            c.productSequence === card.productSequence &&
            c.pricingCard?.title === card.pricingCard?.title &&
            c.pricingCard?.priceTotal === card.pricingCard?.priceTotal
        );

        if (!exists) {
          acc.push(card);
        }
      }
      return acc;
    },
    [] as PricingCardsProps['cards']
  );

  const resolvedToggleVariant: ToggleProps['variant'] =
    toggleVariant ?? 'white';

  return (
    <BlockWrapper {...rest} data-tracker-section={SECTION_NAMES.PRICING_CARDS}>
      <div>
        <BlockHeaderContent {...header} classNames={{ header: '!mb-9' }}>
          <PricingCards
            cards={distinctPricingCards}
            features={features_references}
            contactSalesCards={block.contactSalesCards}
            toggleVariant={resolvedToggleVariant}
          />
        </BlockHeaderContent>
      </div>
    </BlockWrapper>
  );
}
